import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatDistanceToNow, format, isValid } from 'date-fns';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiCalendar, FiUser, FiMessageSquare, FiClock, FiSend, FiPlus, FiTrash2, FiAlertTriangle, FiEdit2 } = FiIcons;

const TaskDetails = ({ task: initialTask, onClose, onEdit }) => {
  const { tasks, addComment, addTimeLog, teamMembers, deleteTask } = useData();
  const { user, hasPermission } = useAuth();
  
  // 1. Hooks
  const [comment, setComment] = useState('');
  const [showTimeLogForm, setShowTimeLogForm] = useState(false);
  const [timeEntry, setTimeEntry] = useState({ hours: '', description: '' });
  const [viewState, setViewState] = useState('details'); // 'details', 'delete_confirm'
  const [isDeleting, setIsDeleting] = useState(false);

  // 2. Data derivation with safety checks
  const task = tasks.find(t => t.id === initialTask.id) || initialTask;
  
  if (!task) return null;

  // Safe accessors for potentially missing fields
  const status = task.status || 'pending';
  const priority = task.priority || 'medium';
  const assignedTo = task.assignedTo || [];

  const assigneeIds = Array.isArray(assignedTo) ? assignedTo : [assignedTo];
  const assignees = assigneeIds.map(id => teamMembers.find(m => String(m.id) === String(id))).filter(Boolean);
  const assigner = teamMembers.find(m => String(m.id) === String(task.assignedBy));

  const handleAddComment = (e) => {
    e.preventDefault();
    if (comment.trim()) {
      addComment(task.id, comment.trim());
      setComment('');
    }
  };

  const handleAddTimeLog = (e) => {
    e.preventDefault();
    if (timeEntry.hours && timeEntry.description.trim()) {
      addTimeLog(task.id, {
        hours: parseFloat(timeEntry.hours),
        description: timeEntry.description.trim()
      });
      setTimeEntry({ hours: '', description: '' });
      setShowTimeLogForm(false);
    }
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteTask(task.id);
      onClose();
    } catch (error) {
      console.error("Error deleting task:", error);
      setIsDeleting(false);
      alert("Failed to delete task. Please try again.");
    }
  };

  const formatDate = (dateString, formatStr = 'MMM dd, yyyy') => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return isValid(date) ? format(date, formatStr) : 'Invalid Date';
  };

  const canDeleteTask = hasPermission('all') || hasPermission('delete_tasks') || task.assignedBy === user.id;
  const isAssignee = assigneeIds.includes(user.id);
  const canEditTask = hasPermission('all') || hasPermission('update_tasks') || task.assignedBy === user.id || isAssignee;
  const totalHours = task.timeLog?.reduce((sum, log) => sum + log.hours, 0) || 0;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[50] p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col md:flex-row relative"
      >
        {/* Delete Confirmation Overlay */}
        <AnimatePresence>
          {viewState === 'delete_confirm' && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-white/95 z-20 flex flex-col items-center justify-center p-8 text-center"
            >
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <SafeIcon icon={FiAlertTriangle} className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Delete this task?</h3>
              <p className="text-gray-600 max-w-md mb-8">
                You are about to delete "<strong>{task.title}</strong>". This action cannot be undone.
              </p>
              <div className="flex space-x-4">
                <button
                  onClick={() => setViewState('details')}
                  className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={isDeleting}
                  className="px-6 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 flex items-center"
                >
                  {isDeleting ? 'Deleting...' : 'Yes, Delete Task'}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Left Panel */}
        <div className="flex-1 flex flex-col h-full max-h-[90vh] overflow-hidden">
          <div className="p-6 border-b border-gray-200 flex justify-between items-start">
            <div>
              <h2 className="text-xl font-bold text-gray-900">{task.title}</h2>
              <div className="flex items-center space-x-3 mt-2">
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${priority === 'high' ? 'bg-red-100 text-red-800' : priority === 'medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                  {priority} Priority
                </span>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${status === 'completed' ? 'bg-green-100 text-green-800' : status === 'in_progress' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                  {String(status).replace('_', ' ')}
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {canEditTask && onEdit && (
                <button onClick={() => onEdit(task)} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                  <SafeIcon icon={FiEdit2} className="w-5 h-5" />
                </button>
              )}
              <button onClick={onClose} className="md:hidden p-2 text-gray-500 hover:bg-gray-100 rounded-lg">
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-8">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-2">Description</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                <p className="text-gray-700 whitespace-pre-wrap">{task.description}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Assigned To</h4>
                {assignees.length > 0 ? (
                  <div className="space-y-3">
                    {assignees.map(member => (
                      <div key={member.id} className="flex items-center space-x-3">
                        <img src={member.avatar || 'https://via.placeholder.com/150'} alt={member.name} className="w-8 h-8 rounded-full bg-gray-200" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{member.name}</p>
                          <p className="text-xs text-gray-500">{member.role?.replace('_', ' ') || 'Member'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">Unassigned</p>
                )}
              </div>
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Details</h4>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <SafeIcon icon={FiCalendar} className="w-4 h-4 text-gray-400" />
                    <span>Due: {formatDate(task.deadline)}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <SafeIcon icon={FiUser} className="w-4 h-4 text-gray-400" />
                    <span>Creator: {assigner?.name || 'Unknown'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Time Tracking Section */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center">
                  <SafeIcon icon={FiClock} className="w-4 h-4 mr-2" /> Time Tracking
                </h3>
                <span className="text-sm font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">{totalHours}h Total</span>
              </div>
              
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <AnimatePresence>
                  {showTimeLogForm ? (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="bg-gray-50 p-4 border-b border-gray-200"
                    >
                      <form onSubmit={handleAddTimeLog} className="space-y-3">
                        <div className="flex space-x-3">
                          <input
                            type="number" step="0.25" min="0"
                            value={timeEntry.hours}
                            onChange={(e) => setTimeEntry({ ...timeEntry, hours: e.target.value })}
                            placeholder="Hours"
                            className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            autoFocus
                          />
                          <input
                            type="text"
                            value={timeEntry.description}
                            onChange={(e) => setTimeEntry({ ...timeEntry, description: e.target.value })}
                            placeholder="Description"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div className="flex justify-end space-x-2">
                          <button type="button" onClick={() => setShowTimeLogForm(false)} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200 rounded-lg">Cancel</button>
                          <button type="submit" disabled={!timeEntry.hours} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">Save</button>
                        </div>
                      </form>
                    </motion.div>
                  ) : (
                    <div className="p-2 bg-gray-50 border-b border-gray-200 flex justify-end">
                      <button onClick={() => setShowTimeLogForm(true)} className="text-sm text-blue-600 font-medium hover:text-blue-700 flex items-center px-2 py-1">
                        <SafeIcon icon={FiPlus} className="w-4 h-4 mr-1" /> Log Time
                      </button>
                    </div>
                  )}
                </AnimatePresence>

                <div className="max-h-60 overflow-y-auto">
                  {task.timeLog?.length > 0 ? (
                    <div className="divide-y divide-gray-100">
                      {[...task.timeLog].reverse().map((log) => {
                        const logUser = teamMembers.find(m => m.id === log.userId);
                        return (
                          <div key={log.id} className="p-3 flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <img src={logUser?.avatar || 'https://via.placeholder.com/32'} alt="" className="w-8 h-8 rounded-full bg-gray-200" />
                              <div>
                                <p className="text-sm text-gray-900 font-medium">{logUser?.name}</p>
                                <p className="text-xs text-gray-500">{log.description}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <span className="block text-sm font-bold text-gray-900">{log.hours}h</span>
                              <span className="block text-xs text-gray-500">{formatDate(log.date, 'MMM dd')}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-6 text-center text-gray-500 text-sm">No time logged yet.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {canDeleteTask && (
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setViewState('delete_confirm')}
                className="text-red-600 text-sm font-medium hover:text-red-800 flex items-center transition-colors"
                title="Delete this task permanently"
              >
                <SafeIcon icon={FiTrash2} className="w-4 h-4 mr-2" /> Delete Task
              </button>
            </div>
          )}
        </div>

        {/* Right Panel - Comments */}
        <div className="w-full md:w-96 border-l border-gray-200 bg-gray-50 flex flex-col h-[500px] md:h-auto">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-white">
            <h3 className="font-semibold text-gray-900 flex items-center">
              <SafeIcon icon={FiMessageSquare} className="w-4 h-4 mr-2" /> Comments ({task.comments?.length || 0})
            </h3>
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500 hidden md:block">
              <SafeIcon icon={FiX} className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {task.comments?.length > 0 ? (
              task.comments.map((comment) => {
                const commentUser = teamMembers.find(m => m.id === comment.userId);
                const isCurrentUser = comment.userId === user.id;
                return (
                  <div key={comment.id} className={`flex space-x-3 ${isCurrentUser ? 'flex-row-reverse space-x-reverse' : ''}`}>
                    <img src={commentUser?.avatar || 'https://via.placeholder.com/32'} alt="" className="w-8 h-8 rounded-full flex-shrink-0 bg-gray-200 mt-1" />
                    <div className={`flex flex-col max-w-[85%] ${isCurrentUser ? 'items-end' : 'items-start'}`}>
                      <div className={`px-4 py-2 rounded-2xl text-sm ${isCurrentUser ? 'bg-blue-600 text-white rounded-tr-none' : 'bg-white border border-gray-200 text-gray-700 rounded-tl-none shadow-sm'}`}>
                        {comment.text}
                      </div>
                      <span className="text-xs text-gray-400 mt-1 px-1">{formatDate(comment.createdAt, 'MMM dd, HH:mm')}</span>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-gray-400">
                <SafeIcon icon={FiMessageSquare} className="w-12 h-12 mb-2 opacity-20" />
                <p className="text-sm">No comments yet</p>
              </div>
            )}
          </div>

          <div className="p-4 bg-white border-t border-gray-200">
            <form onSubmit={handleAddComment} className="relative">
              <input
                type="text"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Write a comment..."
                className="w-full pl-4 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
              />
              <button
                type="submit"
                disabled={!comment.trim()}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                <SafeIcon icon={FiSend} className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default TaskDetails;